import { c as _c } from "react/compiler-runtime";
import { Badge, Box, Button, Circle, Heading, HStack, Icon, Separator, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { usePitchDeckUpload } from '@src/components/sco/PitchDeckAuditor/hooks/usePitchDeckUpload';
import { usePitchDeckSlideAccess } from '@src/components/sco/PitchDeckAuditor/model/access';
import { ratingColorPalette, scoreColorPalette } from '@src/components/sco/PitchDeckAuditor/model/ratingMeta';
import { PitchDeckView } from '@src/components/sco/PitchDeckAuditor/model/types';
import { ExportAuditButton } from '@src/components/sco/PitchDeckAuditor/nav/ExportAuditButton';
import { SlideThumb } from '@src/components/sco/PitchDeckAuditor/nav/SlideThumb';
import { UploadNewVersionDialog } from '@src/components/sco/PitchDeckAuditor/nav/UploadNewVersionDialog';
import { SectionHeading } from '@src/components/sco/PitchDeckAuditor/shared/SectionHeading';
import { LuCircleCheck, LuGauge, LuListChecks, LuLock, LuPresentation, LuRefreshCw } from 'react-icons/lu';
export function DeckNav(t0) {
  const $ = _c(64);
  const {
    deck,
    activeSlideId,
    onSelectSlide,
    onReplaced
  } = t0;
  const {
    isSlideLocked
  } = usePitchDeckSlideAccess();
  const {
    reanalyze,
    reanalyzing
  } = usePitchDeckUpload();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Circle bg="orange.subtle" size={9}><Icon as={LuPresentation} boxSize={4} color="orange.fg" /></Circle>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== deck.companyName) {
    t2 = <Heading lineClamp={1} size="sm">{deck.companyName}</Heading>;
    $[1] = deck.companyName;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== deck.fileName) {
    t3 = deck.fileName && <Text color="fg.muted" fontSize="xs" lineClamp={1}>{deck.fileName}</Text>;
    $[3] = deck.fileName;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <HStack gap={2.5}>{t1}<Box flex={1} minWidth={0}>{t2}{t3}</Box></HStack>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Badge colorPalette="green" size="sm" variant="subtle"><LuCircleCheck />Audit complete</Badge>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== deck.overallScore) {
    t6 = deck.overallScore !== null && <Badge colorPalette={scoreColorPalette(deck.overallScore)} size="sm" variant="surface">{deck.overallScore} / 100</Badge>;
    $[9] = deck.overallScore;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <HStack gap={2} mt={2.5}>{t5}{t6}</HStack>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t4 || $[14] !== t7) {
    t8 = <Box bgColor="colorPalette.bg" borderColor="colorPalette.border" borderRadius="md" borderWidth="1px" flexShrink={0} padding={4}>{t4}{t7}</Box>;
    $[13] = t4;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Separator />;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = activeSlideId === null ? "subtle" : "outline";
  let t11;
  if ($[17] !== onSelectSlide) {
    t11 = () => onSelectSlide(null);
    $[17] = onSelectSlide;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Icon as={LuGauge} />;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== deck.overallRatingKey) {
    t13 = ratingColorPalette(deck.overallRatingKey);
    $[20] = deck.overallRatingKey;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const t14 = deck.overallScore ?? "\u2014";
  let t15;
  if ($[22] !== t13 || $[23] !== t14) {
    t15 = <Badge colorPalette={t13} ml="auto" size="xs" variant="subtle">{t14}</Badge>;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t15) {
    t16 = <Button flexShrink={0} height="fit-content" justifyContent="flex-start" padding={3} size="sm" variant={t10} onClick={t11}>{t12}Deck Overview{t15}</Button>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Separator />;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <SectionHeading icon={LuListChecks}>Slides</SectionHeading>;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== activeSlideId || $[32] !== deck.slides || $[33] !== isSlideLocked || $[34] !== onSelectSlide) {
    let t20;
    if ($[36] !== activeSlideId || $[37] !== isSlideLocked || $[38] !== onSelectSlide) {
      t20 = slide => {
        const isActive = activeSlideId === slide.pitchDeckSlideId;
        const locked = isSlideLocked(slide);
        return <HStack key={slide.pitchDeckSlideId} _hover={{
          bg: isActive ? "colorPalette.card" : "bg.muted",
          opacity: 1
        }} bg={isActive ? "colorPalette.container" : undefined} borderColor={isActive ? "colorPalette.border" : "transparent"} borderRadius="md" borderWidth="1px" cursor="pointer" gap={2.5} opacity={isActive ? 1 : 0.7} padding={1.5} onClick={() => onSelectSlide(slide.pitchDeckSlideId)}><SlideThumb isActive={isActive} slide={slide} /><Box flex={1} minWidth={0}><HStack gap={1.5}><Text flex={1} fontSize="sm" fontWeight={isActive ? "semibold" : "medium"} lineClamp={1} minWidth={0}>{slide.classificationLabel || `Slide #${slide.position}`}</Text>{locked && <Icon aria-label="Upgrade to unlock" as={LuLock} boxSize={3} color="colorPalette.fg/50" flexShrink={0} />}</HStack><HStack gap={1.5} mt={1}><Box bg={`${ratingColorPalette(slide.ratingKey)}.solid`} borderRadius="full" boxSize={2} /><Text color="colorPalette.fg/50" lineClamp={1} textStyle="label2xs">{slide.ratingLabel}{slide.score !== null ? ` · ${slide.score}` : ""}</Text></HStack></Box></HStack>;
      };
      $[36] = activeSlideId;
      $[37] = isSlideLocked;
      $[38] = onSelectSlide;
      $[39] = t20;
    } else {
      t20 = $[39];
    }
    t19 = deck.slides.map(t20);
    $[31] = activeSlideId;
    $[32] = deck.slides;
    $[33] = isSlideLocked;
    $[34] = onSelectSlide;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[40] !== t19) {
    t20 = <VStack align="stretch" flex={1} minHeight={0}>{t18}<VStack align="stretch" flexGrow={1} gap={1} minHeight={0} mt={2} overflowY="auto">{t19}</VStack></VStack>;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Separator flexShrink={0} />;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== deck.pitchDeckId || $[44] !== onReplaced) {
    t22 = <UploadNewVersionDialog pitchDeckId={deck.pitchDeckId} onReplaced={onReplaced} />;
    $[43] = deck.pitchDeckId;
    $[44] = onReplaced;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== deck) {
    t23 = <ExportAuditButton deck={deck} />;
    $[46] = deck;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== deck.pitchDeckId || $[49] !== reanalyze) {
    t24 = () => void reanalyze(deck.pitchDeckId).catch(_temp);
    $[48] = deck.pitchDeckId;
    $[49] = reanalyze;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <LuRefreshCw />;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== reanalyzing || $[53] !== t24) {
    t26 = <Button flexShrink={0} loading={reanalyzing} loadingText={"Re-running analysis\u2026"} size="xs" variant="outlineGhost" onClick={t24}>{t25}Re-run analysis</Button>;
    $[52] = reanalyzing;
    $[53] = t24;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== t23 || $[56] !== t26) {
    t27 = <SimpleGrid columns={2} gap={4}>{t23}{t26}</SimpleGrid>;
    $[55] = t23;
    $[56] = t26;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== t16 || $[59] !== t20 || $[60] !== t22 || $[61] !== t27 || $[62] !== t8) {
    t28 = <VStack align="stretch" gap={4} height="full" minHeight={0} paddingX={2} width="full">{t8}{t9}{t16}{t17}{t20}{t21}{t22}{t27}</VStack>;
    $[58] = t16;
    $[59] = t20;
    $[60] = t22;
    $[61] = t27;
    $[62] = t8;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  return t28;
}
function _temp() {}